import React, { useMemo } from 'react';
import { Pressable, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import moment from 'moment';

import Text from '../../../components/text/Text';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { STATUS } from '../../../../common/constants';
import { StatusBadge } from '../../../design/components';
import { getUserName } from '../../../../common/utils/user/getUserName';
import { getStatusLabel } from '../../../utils/statusLabel';

const MachineSetupListItem = ({ item }) => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);

	const dueDate = useMemo(() => {
		if (!item?.locationSetup?.dueDate) {
			return null;
		}

		return moment(item.locationSetup.dueDate).format('DD MMM YYYY');
	}, [item?.locationSetup?.dueDate]);

	const statusLabel = getStatusLabel(item?.status, 'Unknown status');
	const createdLabel = useMemo(() => {
		if (!item?.created) {
			return 'Unknown date';
		}

		return moment(item.created).format('DD MMM YYYY');
	}, [item?.created]);

	const creatorName = useMemo(() => {
		return item?.creator ? getUserName(item.creator) : null;
	}, [item?.creator]);
	const assignedMachine = item?.machine || item?.plannedMachine;

	return (
		<Pressable
			onPress={() => navigation.navigate('MachineSetupDetailView', {
				machineSetupId: item?.id,
				title: item?.identifier,
			})}
			accessibilityRole="button"
			style={({ pressed }) => [styles.container, pressed && styles.pressed]}
		>
			<View style={styles.headerRow}>
				<Text style={styles.identifier} numberOfLines={1}>
					{item?.identifier || 'Unnamed setup'}
				</Text>
				<StatusBadge
					label={statusLabel}
					tone={item?.status === STATUS.COMPLETE ? 'success' : 'neutral'}
					size="small"
				/>
			</View>
			{
				dueDate && (
					<Text style={styles.subtleText} numberOfLines={1}>
						Due {dueDate}
					</Text>
				)
			}
			<Text style={styles.subtleText} numberOfLines={1}>
				Location setup: {item?.locationSetup?.identifier || 'Unknown'}
			</Text>
			<Text style={styles.secondary} numberOfLines={1}>
				Location: {item?.locationSetup?.location?.name || 'No location'}
			</Text>
			<Text style={styles.subtleText} numberOfLines={1}>
				Place: {item?.place?.name || 'No place selected'}
			</Text>
			<Text style={styles.subtleText} numberOfLines={1}>
				Machine: {assignedMachine?.assetId || assignedMachine?.type || 'No machine selected'}
			</Text>
			{!item?.machine && item?.plannedMachine && (
				<Text style={styles.subtleText} numberOfLines={1}>Move planned</Text>
			)}
			{
				item?.assignees?.length > 0 && (
					<Text style={styles.subtleText} numberOfLines={2}>
						Assigned to {item.assignees.map(assignee => assignee?.firstname || assignee?.username).filter(Boolean).join(', ')}
					</Text>
				)
			}
			<Text style={styles.subtleText} numberOfLines={1}>
				Created {createdLabel}
			</Text>
			{
				creatorName && (
					<Text style={styles.subtleText} numberOfLines={1}>
						Creator: {creatorName}
					</Text>
				)
			}
		</Pressable>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	container: {
		marginHorizontal: space.gutter,
		marginBottom: space.sm,
		paddingHorizontal: 14,
		paddingVertical: space.md,
		gap: space.xs,
		borderRadius: radius.card,
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		backgroundColor: colors.raised,
	},
	pressed: {
		opacity: 0.85,
	},
	headerRow: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: space.md,
		marginBottom: space.xxs,
	},
	identifier: {
		...type.rowTitleSemibold,
		color: colors.text,
		flex: 1,
	},
	secondary: {
		...type.bodySmall,
		color: colors.text,
	},
	subtleText: {
		...type.secondary,
		color: colors.textSecondary,
	},
});

export default MachineSetupListItem;
